import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck, AlertCircle, ArrowLeft, Check, Mail } from "lucide-react";
import { publicApi, errMsg, USER_TOKEN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

function PasswordInput({ value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required
        data-testid={testId}
        autoComplete={autoComplete}
        className="h-11 pr-10"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-0.5 top-0.5 h-10 w-10"
        onClick={() => setShow(!show)}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function ForgotPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState("request"); // request | verify | done
  const [email, setEmail] = useState(sp.get("email") || "");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const { setUser } = useAuth();

  useEffect(() => {
    if (!wait) return;
    const t = setInterval(() => setWait((w) => (w > 0 ? w - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [wait]);

  const requestCode = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setErr("");
    try {
      const r = await publicApi.post("/auth/password/reset/request", { email: email.trim() });
      setChallenge(r.data);
      setWait(r.data.resend_after || 30);
      setStep("verify");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (wait > 0 || !challenge) return;
    setErr("");
    try {
      const r = await publicApi.post("/auth/otp/send", {
        challenge_id: challenge.challenge_id,
        channel: "email",
      });
      setChallenge(r.data);
      setWait(r.data.resend_after || 30);
    } catch (e2) {
      // Shadow challenges have no real OTP — the generic error below is fine
      setErr(errMsg(e2));
    }
  };

  const confirm = async (e) => {
    e?.preventDefault?.();
    if (newPw.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    if (code.length !== 6) {
      setErr("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const r = await publicApi.post("/auth/password/reset/confirm", {
        challenge_id: challenge.challenge_id,
        code,
        new_password: newPw,
      });
      // Auto-sign in
      localStorage.setItem(USER_TOKEN, r.data.token);
      setUser?.(r.data.user);
      setStep("done");
      setTimeout(() => nav("/dashboard"), 1200);
    } catch (e2) {
      setErr(errMsg(e2));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-bg flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-block" data-testid="forgot-logo-link">
          <Logo />
        </Link>
        <Card className="shadow-[0_10px_30px_hsl(222_35%_12%/0.10)]" data-testid="forgot-password-card">
          <CardContent className="p-6 sm:p-8">
            {step === "request" && (
              <>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <h1 className="mt-4 text-2xl font-semibold">Reset your password</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the email on your account. We'll send a 6-digit code to confirm it's you.
                </p>
                {err && (
                  <Alert variant="destructive" className="mt-4" data-testid="forgot-request-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{err}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={requestCode} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-lift h-11 w-full"
                    disabled={busy || !email.trim()}
                    data-testid="forgot-send-code-button"
                  >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send verification code
                  </Button>
                </form>
                <p className="mt-6 text-sm text-muted-foreground">
                  Remembered it?{" "}
                  <Link to="/login" className="font-medium text-primary" data-testid="forgot-back-login-link">
                    Back to sign in
                  </Link>
                </p>
              </>
            )}

            {step === "verify" && challenge && (
              <div data-testid="forgot-verify-step">
                <button
                  type="button"
                  onClick={() => { setStep("request"); setChallenge(null); setCode(""); setErr(""); }}
                  className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="forgot-back-button"
                >
                  <ArrowLeft className="h-3 w-3" /> Back
                </button>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </div>
                <h1 className="mt-4 text-2xl font-semibold">Enter the code &amp; new password</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-foreground" data-testid="forgot-destination">
                    {challenge.destination}
                  </span>
                  . It expires in 10 minutes.
                </p>
                {err && (
                  <Alert variant="destructive" className="mt-4" data-testid="forgot-verify-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{err}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={confirm} className="mt-6 space-y-5">
                  <div className="space-y-1.5">
                    <Label>Verification code</Label>
                    <InputOTP
                      maxLength={6}
                      value={code}
                      onChange={(v) => setCode(v.replace(/\D/g, ""))}
                      inputMode="numeric"
                      data-testid="forgot-otp-input"
                      autoFocus
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <div className="space-y-1.5">
                    <Label>New password</Label>
                    <PasswordInput
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      testId="forgot-new-password-input"
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                  </div>
                  <Button
                    type="submit"
                    className="btn-lift h-11 w-full"
                    disabled={busy || code.length !== 6 || newPw.length < 8}
                    data-testid="forgot-reset-submit-button"
                  >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Reset password &amp; sign in
                  </Button>
                </form>
                <div className="mt-4 text-sm">
                  <button
                    type="button"
                    disabled={wait > 0}
                    onClick={resend}
                    className="font-medium text-primary disabled:text-muted-foreground"
                    data-testid="forgot-resend-button"
                  >
                    {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
                  </button>
                </div>
              </div>
            )}

            {step === "done" && (
              <div className="text-center" data-testid="forgot-done-step">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <Check className="h-7 w-7" />
                </div>
                <h1 className="mt-4 text-2xl font-semibold">Password updated</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  You're signed in. Taking you to your dashboard…
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
